import React from "react";
import styles from "../../../common/recommendationStyles";
import { withStyles } from "@material-ui/core/styles";
import DriverRecommendationCard from "../../../common/DriverRecommendationCard";
import { postData } from "../../../../../../../../api/componentAPIs";
import {
  DialogTitle as MuiDialogTitle,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../../../../userProfile/userProfileContext";
import appConstants from "../../../../../../../../appConstants";
import ManualDriverAssignment from "../../../common/ManualDriverAssignment";
import constants from "../../../constants";
import { observer, inject } from "mobx-react";
import { getFormattedRouteRecommendationData } from "./utils";
import { DialogActions } from "../../components";
import RefreshIconButton from "../../../../../../../common/RefreshIconButton";

const dialogStyles = (theme) => ({
  root: {
    display: 'flex',
    padding: '8 0',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogTitle: {
    color: theme.HeaderSelectedText,
    fontSize: theme.headerTextFontSize,
    fontWeight: 600,
  },
  refreshIcon: {
    '& i.icon-refresh-1': {
      color: theme.iconColor,
    },
  }
});

const DialogTitle = withStyles(dialogStyles)((props) => {
  const {
    children,
    classes,
    handleRefresh,
    hasDispatchedDrivers,
    loading,
    ...other
  } = props;
  const isDisabled = !hasDispatchedDrivers || loading;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography className={classes.dialogTitle}>{children}</Typography>
      <div className={classes.refreshIcon}>
        <RefreshIconButton
          isDisabled={isDisabled}
          isRefreshing={loading}
          onRefresh={handleRefresh}
          defaultTooltip={constants.API_MESSAGES.REFRESH_RECOMMENDATION}
          refreshingTooltip={constants.API_MESSAGES.FETCH_IN_PROGRESS}
        />
      </div>
    </MuiDialogTitle>
  );
});


@inject('DispatchStore')
@observer
class DriverRecommendationsPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      errorMsg: '',
      recommendationData: [],
      driverRecommendationDetails: {}
    };
  }

  componentDidMount() {
    this.fetchRecommendation();
  }

  fetchRecommendation = () => {
    const requestPayload = {
      groupId: this.props.selectedPickupInfo.groupId,
      terminalCode: this.props.userData.userProfile.terminal.code
    };
    postData('Recommendations', requestPayload).then(
      (res) => {
        if (res?.status >= 200 && res.status < 400) {
          const { pickupsList, currentActiveRoutes } = this.props.DispatchStore;
          const { recommendationData, unassignedPickups } = getFormattedRouteRecommendationData({
            recommendationRoutes: res.data,
            pickupsList,
            currentActiveRoutes,
            pickupId: this.props.selectedPickupInfo.pickupId
          });
          this.setRecommendationInfo({ recommendationData, unassignedPickups });

          // select feasible recommended drivers in driver grid
          const driverIdList = this.getTopFeasibleRecommendedDrivers(recommendationData).map((r) =>
            r.driverRecommendationDetails.driverId.toUpperCase()
          );
          if (driverIdList.length) {
            this.props.glEventHub.emit('SelectDriverRoutes', driverIdList);
          }
          return;
        }
        this.setRecommendationInfo({
          errorMsg: constants.API_MESSAGES.FAILED_TO_FETCH
        });
      },
      (err) => {
        const backendError = err?.error?.message ?? '';
        const engineError = err?.data?.error?.detail?.message ?? '';
        this.setRecommendationInfo({
          errorMsg: backendError || engineError
        });
      }
    );
  };

  getBestFeasibleDriver = (recommendationData) => {
    return (
      recommendationData.find(
        (d) =>
          d.driverRecommendationDetails.recommendationRank === 1 &&
          d.driverRecommendationDetails.isFeasibleOption
      ) || {}
    );
  };

  setRecommendationInfo = ({
    loading = false,
    errorMsg = '',
    recommendationData = [],
    unassignedPickups = []
  }) => {
    const { driverRoutePreview, driverRecommendationDetails = {} } =
      this.getBestFeasibleDriver(recommendationData);
    this.showSelectedDriverRouteOnMap({
      driverRoutePreview,
      unassignedPickups,
      mapConfig: {
        loading,
        isFeasibleOption: driverRecommendationDetails.isFeasibleOption || false
      }
    });
    this.setState({
      loading,
      errorMsg,
      recommendationData,
      driverRecommendationDetails
    });
  };

  handleRefresh = () => {
    this.setState({
      loading: true,
      errorMsg: '',
      recommendationData: [],
      driverRecommendationDetails: {}
    });
    this.showSelectedDriverRouteOnMap({
      driverRoutePreview: {},
      unassignedPickups: [],
      mapConfig: { loading: true }
    });
    this.fetchRecommendation();
  };

  getTopFeasibleRecommendedDrivers = (recommendationData) => {
    /* return top 5 feasible recommended drivers */
    return recommendationData
      .filter((r) => r.driverRecommendationDetails.isFeasibleOption)
      .slice(0, appConstants.MAX_FEASIBLE_DRIVERS);
  };

  renderRecommendationLabel = (feasibleDriverRecommendationCount) => {
    const { loading, errorMsg, recommendationData } = this.state;
    const { classes } = this.props;
    if (loading) {
      return null;
    }
    if (errorMsg) {
      return (
        <div className={classes.noRecommendations}>
          <div>{constants.API_MESSAGES.RECOMMENDATION_FAILED}</div>
          <span className={classes.error}>Error : {errorMsg}</span>
        </div>
      );
    } else if (!this.props.DispatchStore.dispatchedDrivers.length) {
      return (
        <div className={classes.noRecommendations}>{constants.API_MESSAGES.NO_ACTIVE_DRIVERS}</div>
      );
    } else if (recommendationData.length === 0) {
      return (
        <div className={classes.noRecommendations}>
          {constants.API_MESSAGES.NO_RECOMMENDATION_AVAILABLE}
        </div>
      );
    } else if (!feasibleDriverRecommendationCount) {
      return (
        <div className={classes.noRecommendations}>
          {constants.API_MESSAGES.NO_FEASIBLE_DRIVERS}
        </div>
      );
    }
    return null;
  };

  updateRoutePreview = (recommendationInfo, isFeasibleOption) => {
    const { loading } = this.state;
    const { driverRoutePreview, driverRecommendationDetails = {} } = recommendationInfo;
    this.setState({ driverRecommendationDetails });
    this.showSelectedDriverRouteOnMap({
      driverRoutePreview,
      mapConfig: {
        loading,
        isFeasibleOption
      }
    });
  };

  isFeasibleDriverCardHighlighted = (driverId) => {
    return this.state.driverRecommendationDetails.driverId === driverId;
  };

  showSelectedDriverRouteOnMap = ({ driverRoutePreview, mapConfig, unassignedPickups }) => {
    this.props.handleRoutePreview({ driverRoutePreview, mapConfig, unassignedPickups});
  };

  handleDriverAssignment = (driverId) => {
    this.setState({ driverRecommendationDetails: {} });
    this.props.assignPickup(driverId);
  };

  render() {
    const { classes } = this.props;
    const { loading, recommendationData } = this.state;
    const topFeasibleDriverRecommendations =
      this.getTopFeasibleRecommendedDrivers(recommendationData);
    const preferredDriverRecommendationsCount = topFeasibleDriverRecommendations.length;

    return (
      <div className={classes.allRecommendationContainer}>
        <DialogTitle
          id="customized-dialog-title"
          loading={loading}
          handleRefresh={this.handleRefresh}
          hasDispatchedDrivers={this.props.DispatchStore.dispatchedDrivers.length}>
          Recommended Drivers
        </DialogTitle>
        <div className={classes.container}>
          {loading ? (
            <div className={classes.loaderContainer}>
              <CircularProgress size={24} className={classes.loader} />
              {constants.API_MESSAGES.FETCH_IN_PROGRESS}
            </div>
          ) : (
            <div className={`${classes.innerAllRecommendationContainer} customScroll`}>
              {topFeasibleDriverRecommendations.map((r) => (
                <DriverRecommendationCard
                  key={`Recommendation Route_${r.driverRecommendationDetails.routeId} `}
                  recommendationButtonLabel={constants.RECOMMENDATION_BUTTON_LABEL.PREVIEW}
                  data={r.driverRecommendationDetails}
                  onClick={() =>
                    this.updateRoutePreview(
                      r,
                      r.driverRecommendationDetails.isFeasibleOption
                    )
                  }
                  highlightPreviewCard={this.isFeasibleDriverCardHighlighted(
                    r.driverRecommendationDetails.driverId
                  )}
                />
              ))}
              {this.renderRecommendationLabel(preferredDriverRecommendationsCount)}
              {recommendationData.length > 0 && (
                <ManualDriverAssignment
                  handleManualDriverOptionChange={this.updateRoutePreview}
                  recommendationData={recommendationData}
                  dispatchedDrivers={this.props.DispatchStore.dispatchedDrivers}
                  glEventHub={this.props.glEventHub}
                  isFeasibleDriverCardHighlighted={this.isFeasibleDriverCardHighlighted}
                />
              )}
            </div>
          )}
          <DialogActions
            onClose={this.props.handleClose}
            onConfirm={() =>
              this.handleDriverAssignment(this.state.driverRecommendationDetails.driverId)
            }
            isDisabled={loading || !this.state.driverRecommendationDetails.driverId}
          />
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withUserProfile
)(DriverRecommendationsPanel);
